import React from 'react';
import get from 'lodash/get';
import { FormikProps } from 'formik';
import { withInject } from '@piwikpro/platform';
import { TFunction } from 'react-i18next';
import {
  AutoSavedTextFieldControl,
  Stack,
  withTranslation,
} from '@piwikpro/ui-components';
import { TrackerSettings, TrackerSettingsAttributes, TrackerSettingsResponse } from '../../../TrackerSettings';
import FailMessage from '../../GlobalAppSettings/FailMessage';

const defaultValue = 43200;
const minValue = 1;
const maxValue = 43200;

function FieldError(fieldName: string, message: string) {
  const error: any = new Error(message);

  error.errors = {
    [fieldName]: message,
  };

  return error;
}

export interface MaxSessionTimeProps {
  settings: TrackerSettingsResponse
  trackerSettings: TrackerSettings
  failed: boolean
  t: TFunction
}

export class MaxSessionTime extends React.Component<MaxSessionTimeProps> {
  static readonly attributeName = 'session_max_duration_seconds'

  static readonly fieldName = `${this.attributeName }_string`;

  static readonly minuteDivisor = 60;

  static readonly hourDivisor = 60 * this.minuteDivisor;

  constructor(props: MaxSessionTimeProps) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  async handleSubmit(values: Partial<TrackerSettingsAttributes>, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }: FormikProps<TrackerSettingsAttributes>) {
    const { trackerSettings } = this.props;

    try {
      const value = this.getParsedValue(values);
      setFieldValue(MaxSessionTime.attributeName, value);
      setFieldValue(
        MaxSessionTime.fieldName,
        this.getFormattedValue(value),
        false,
      );

      await trackerSettings.updateSettings({
        [MaxSessionTime.attributeName]: value,
      });
    } catch (err: any) {
      setErrors(err.errors);
      setFieldValue(
        MaxSessionTime.fieldName,
        this.getFormattedValue(),
        false,
      );
    }

    setSubmitting(false);
  }

  getTimeUnits() {
    const { t } = this.props;

    return {
      hourUnit: t('apps.details.settings.session-limits.max-session-time.unit.hour'),
      minuteUnit: t('apps.details.settings.session-limits.max-session-time.unit.minute'),
      secondUnit: t('apps.details.settings.session-limits.max-session-time.unit.second'),
    };
  }

  getFormattedValue(newValue: number | null = null) {
    const { settings } = this.props;
    const value = newValue === null
      ? get(settings.attributes, MaxSessionTime.attributeName, defaultValue)
      : newValue;

    const hours = Math.floor(value / MaxSessionTime.hourDivisor);
    const minutes = Math.floor((value % MaxSessionTime.hourDivisor) / MaxSessionTime.minuteDivisor);
    const seconds = (
      value - hours * MaxSessionTime.hourDivisor - minutes * MaxSessionTime.minuteDivisor
    );

    const units = this.getTimeUnits();

    return [
      [hours, units.hourUnit],
      [minutes, units.minuteUnit],
      [seconds, units.secondUnit],
    ].filter(([val]) => val !== 0).map(([val, unit]) => `${val}${unit}`).join(' ');
  }

  getParsedValue(values: Partial<TrackerSettingsAttributes>) {
    const { t } = this.props;
    const value = get(values, MaxSessionTime.fieldName);
    const regex = /^(\d+)([\w.]+)$/;

    if (value) {
      const unitArray = value.split(' ').map((entry: string) => [entry, regex.exec(entry)]);

      let hours = 0;
      let minutes = 0;
      let seconds = 0;

      const units = this.getTimeUnits();

      unitArray.forEach(([entry, match]: [entry: any, match: any]) => {
        if (entry) {
          if (match) {
            const [, unitValue, unitSymbol] = match;
            const numberValue = Number(unitValue);

            if (!Number.isNaN(numberValue)) {
              switch (unitSymbol) {
                case units.hourUnit:
                  hours += Number(numberValue);
                  break;
                case units.minuteUnit:
                  minutes += numberValue;
                  break;
                case units.secondUnit:
                  seconds += numberValue;
                  break;
                default:
                  throw FieldError(
                    MaxSessionTime.fieldName,
                    t('apps.details.settings.session-limits.max-session-time.error.unit', {
                      invalidToken: unitSymbol,
                      rawInput: value,
                    }),
                  );
              }
            }
          } else {
            throw FieldError(
              MaxSessionTime.fieldName,
              t('apps.details.settings.session-limits.max-session-time.error.symbol', {
                invalidToken: entry,
                rawInput: value,
              }),
            );
          }
        }
      });

      const result = (
        hours * MaxSessionTime.hourDivisor + minutes * MaxSessionTime.minuteDivisor + seconds
      );

      if (result > maxValue) {
        throw FieldError(
          MaxSessionTime.fieldName,
          t('apps.details.settings.session-limits.max-session-time.error.maximum', {
            maxValue: this.getFormattedValue(maxValue),
          }),
        );
      }

      if (result < minValue) {
        throw FieldError(
          MaxSessionTime.fieldName,
          t('apps.details.settings.session-limits.max-session-time.error.minimum', {
            minValue: this.getFormattedValue(minValue),
          }),
        );
      }

      return result;
    }

    return NaN;
  }

  render() {
    const { failed, t } = this.props;

    return (
      <>
        <Stack vertical>
          {!failed && (
            <Stack.Item fill>
              <AutoSavedTextFieldControl
                id="app-settings-max-session-time"
                label={t('apps.details.settings.session-limits.max-session-time.title')}
                info={t('apps.details.settings.session-limits.max-session-time.description')}
                name={MaxSessionTime.fieldName}
                initialValue={this.getFormattedValue()}
                onSubmit={this.handleSubmit}
              />
            </Stack.Item>
          )}
        </Stack>
        {failed && <FailMessage />}
      </>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(withTranslation(['administration'])(MaxSessionTime as any));
